import React from "react";
import {Paper, Table, TableHead, TableRow, TableCell, TableBody} from "@material-ui/core";
import {observer} from "mobx-react";
import {observable} from "mobx";
import {DbsPermissionsDynamic} from "./DbsPermissionsDynamic";
import {getAllPermissions} from "../utils/getAllPermissions";
import {getAsteriskPermissions} from "../utils/getAsteriskPermissions";
import {getNonAsteriskPermissions} from "../utils/getNonAsteriskPermissions";
import {getCheckboxControl} from "../utils/getCheckboxControl";
import {EntryAdder} from "../../../../app/components/EntryAdder";
import {AppState} from "../../../../app/types";

export const DatabasePermissions = observer(
    (p: {permissions: {[key: string]: string[]}; appState: AppState} & {classes?: any}) => {
        const tempDbs = observable([] as string[]);
        const {permissions, appState} = p;
        const updateUsers = appState.actions.users && appState.actions.users.updateUsers;
        if (!updateUsers) {
            console.error(`appState.actions.users.updateUsers not found`);
            return null;
        }
        const allPermissions = getAllPermissions(permissions, "db");
        const asteriskPermissions = getAsteriskPermissions(allPermissions);
        const dbsPermissions = getNonAsteriskPermissions(allPermissions);

        const togglePermission = (type: string) => (value: string) => (isOn: boolean) => {
            const entry = `/db/${value}/${type}`;
            Object.keys(permissions).forEach(key => {
                const userPermissions = permissions[key];
                isOn
                    ? !userPermissions.includes(entry) && userPermissions.push(entry)
                    : userPermissions.includes(entry) && userPermissions.splice(userPermissions.indexOf(entry), 1);
            });
            updateUsers(permissions);
        };

        const toggleAsterisk = togglePermission("*");

        return (
            <Paper elevation={0}>
                <Table padding="none">
                    <TableHead>
                        <TableRow>
                            <TableCell key="cell0">Database</TableCell>
                            <TableCell key="cell1">View</TableCell>
                            <TableCell key="cell2">Update</TableCell>
                            <TableCell key="cell3">Deploy</TableCell>
                            <TableCell key="cell5">Create</TableCell>
                            <TableCell key="cell6">Delete</TableCell>
                            <TableCell key="cell4">Manage</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow key={`row *`}>
                            <TableCell>*</TableCell>
                            <TableCell>{getCheckboxControl(asteriskPermissions, "view", toggleAsterisk)}</TableCell>
                            <TableCell>{getCheckboxControl(asteriskPermissions, "update", toggleAsterisk)}</TableCell>
                            <TableCell>{getCheckboxControl(asteriskPermissions, "deploy", toggleAsterisk)}</TableCell>
                            <TableCell>{getCheckboxControl(asteriskPermissions, "create", toggleAsterisk)}</TableCell>
                            <TableCell>{getCheckboxControl(asteriskPermissions, "delete", toggleAsterisk)}</TableCell>
                            <TableCell>{getCheckboxControl(asteriskPermissions, "manage", toggleAsterisk)}</TableCell>
                        </TableRow>
                        <DbsPermissionsDynamic {...{dbsPermissions, asteriskPermissions, tempDbs, togglePermission}} />
                        <EntryAddedWrapper {...{appState, dbsPermissions, tempDbs}} />
                    </TableBody>
                </Table>
            </Paper>
        );
    }
);

const EntryAddedWrapper = observer(
    (p: {
        appState: AppState;
        dbsPermissions: {
            [key: string]: Map<string, string[]>;
        };
        tempDbs: string[];
    }) => {
        const {appState, dbsPermissions, tempDbs} = p;
        return (
            <TableRow key={`row with button`}>
                <TableCell colSpan={7}>
                    <EntryAdder
                        {...{
                            isReady: !!appState.data.databases.length,
                            onChange: (value: string) => tempDbs.push(value),
                            options: appState.data.databases
                                .map(db => db.name)
                                .filter(
                                    name =>
                                        !tempDbs.includes(name) &&
                                        !Object.keys(dbsPermissions).some(userId => dbsPermissions[userId].has(name))
                                )
                                .map((name, i) => {
                                    return {
                                        value: i.toString(),
                                        label: name
                                    };
                                }),
                            placeholderText: "Choose a database to add",
                            placeholderNotReadyText: "Please wait for databases to load"
                        }}
                    />
                </TableCell>
            </TableRow>
        );
    }
);
